import { Component, OnInit, ViewChild } from '@angular/core'
import { AuthManager } from '../shared/auth/AuthManager'
import {
   AppUser,
   EmployeeTypeMapApiClient,
   DimEmployeeType,
   EmployeeTypeMapInfo,
   SearchEmployeeTypeMapPayload,
   AppPractice,
   ApplicationArea,
   AuthApiClient,
} from '../shared/services/ppv.api'
import { forkJoin, Subject, Subscription } from 'rxjs'
import { Table } from 'primeng/table'
import { ConfirmationService, LazyLoadEvent, SelectItem } from 'primeng/api'
import { ToastrService } from 'ngx-toastr'

@Component({
   selector: 'app-employee-type-map',
   templateUrl: './employee-type-map.component.html',
   styleUrls: ['./employee-type-map.component.scss'],
   standalone: false,
})
export class EmployeeTypeMapComponent implements OnInit {
   public isLoading: boolean = true
   public searchTerm: string
   public employeeTypeFilters: string[]
   public regionFilters: string[]
   public locationFilters: string[]
   public saving: boolean = false
   currentUser: AppUser
   employeeTypes: SelectItem[]

   items: EmployeeTypeMapInfo[] = []
   public practices: AppPractice[] = []
   public filteredPractices: number[] = []
   public filteredEmployeeTypes: number[] = []

   public showOnlyNoDayforceID: boolean = true

   pageRequested = new Subject<LazyLoadEvent>()

   subscriptions: Subscription[] = []

   public tableLoading = 0
   public pageSize = 200

   assignRevenueTos: any[]
   dayforceResults: any[]

   public isDirty: boolean

   @ViewChild('dt') table: Table

   constructor(
      private authManager: AuthManager,
      private employeeTypeMapClient: EmployeeTypeMapApiClient,
      private authApiClient: AuthApiClient,
      private confirmationService: ConfirmationService,
      private toastrSvc: ToastrService
   ) {}

   ngOnInit(): void {
      this.subscriptions.push(this.pageRequested.subscribe((newPage) => this.loadData(newPage)))

      forkJoin({
         currentUser: this.authManager.getCurrentUser(),
         employeeTypes: this.employeeTypeMapClient.getEmployeeTypes(),
         ap: this.authApiClient.getActivePractices(ApplicationArea.EmployeeTypeMap),
      }).subscribe((r) => {
         this.currentUser = r.currentUser
         this.employeeTypes = r.employeeTypes.map((et) => {
            return { label: et.employeeTypeName, value: et.employeeTypeSkey }
         })

         this.practices = this.currentUser.practices.filter((p) => r.ap.indexOf(p.id) > -1).sort((x, y) => x.nameId.localeCompare(y.nameId))

         this.isLoading = false
      })
   }

   public searchEmployees(practiceSkey: number, event: any) {
      this.employeeTypeMapClient
         .searchEmployees({
            skip: 0,
            take: 100,
            practices: [practiceSkey],
            orderDirection: 1,
            orderBy: '',
            filter: event.query,
            employeeTypes: [],
            showOnlyNoDayforceID: false,
         })
         .subscribe((results) => {
            this.assignRevenueTos = results.data.map((r) => {
               return {
                  label: `${r.firstName} ${r.lastName} (${r.initials})`,
                  id: r.origSourceSkey,
               }
            })
         })
   }

   public searchDayforceEmployees(practiceSkey: number, event: any) {
      this.employeeTypeMapClient.searchDayforceEmployeeInfo(practiceSkey, event.query).subscribe((results) => {
         this.dayforceResults = results.map((r) => {
            let label = `${r.displayName} (${r.dayforceId})`

            if (r.practiceSKey === practiceSkey) label = label + ' (In Practice)'

            return {
               label,
               id: r.dayforceId,
            }
         })
      })
   }

   private skipNextLoad: boolean

   public loadData(event: LazyLoadEvent) {
      console.log('load event triggered', JSON.stringify(event))
      this.tableLoading++
      this.employeeTypeMapClient
         .searchEmployees({
            skip: event.first ?? 0,
            take: event.rows ?? this.pageSize,
            practices: this.filteredPractices,
            orderDirection: event.sortOrder,
            orderBy: event.sortField,
            filter: this.searchTerm,
            employeeTypes: this.filteredEmployeeTypes,
            showOnlyNoDayforceID: this.showOnlyNoDayforceID,
         })
         .subscribe((results) => {
            results.data.forEach((v: any) => {
               v.dayforceResult = {
                  id: v.dayforceId,
                  label: v.dayforceDisplay,
               }
            })
            Array.prototype.splice.apply(this.items, [...[event.first, event.rows], ...results.data])

            this.items = [...this.items].filter((i) => i && i.practiceName)

            this.tableLoading--
         })
   }

   resetTable() {
      if (this.skipNextLoad) {
         this.skipNextLoad = false
         console.log('skipping')
         return
      }
      this.items = []
      this.isDirty = false
      this.table.resetScrollTop()
      // this.table.clearCache();
      this.table.filter(undefined, '', '')
      //this.table.clear();
   }

   onPracticeChange(event) {
      this.filteredPractices = event.value.map((x) => x)
      this.applyFilters()
   }

   onEmployeeTypeChange(event) {
      this.filteredEmployeeTypes = event.value
      this.applyFilters()
   }

   onFilterShowOnlyNoDayforceID(checked) {
      this.showOnlyNoDayforceID = checked
      this.applyFilters()
   }

   onSerachChange(event) {
      this.applyFilters()
   }

   onSort() {
      this.skipNextLoad = true
      this.applyFilters()
   }

   onItemChange(item) {
      item.dirty = true
      this.isDirty = true
   }

   onDayforceIdChange(item: EmployeeTypeMapInfo, $event) {
      const value = $event.value
      console.log({ item, value })
      item.dayforceId = value.id
      this.onItemChange(item)
   }

   confirmSaveBeforeFiltering() {
      return new Promise<boolean>((res, rej) => {
         if (this.isDirty) {
            this.confirmationService.confirm({
               message: "You have unsaved changes. Do you want to save? (If you don't save, your changes will be lost)",
               accept: () => {
                  res(true)
               },
               reject: () => {
                  res(false)
               },
               acceptLabel: 'Yes',
               rejectLabel: 'No',
               defaultFocus: 'Yes',
            })
         } else {
            res(false)
         }
      })
   }

   async applyFilters() {
      var saveNeeded = await this.confirmSaveBeforeFiltering()
      if (saveNeeded) {
         this.doSave(() => {
            this.resetTable()
         })
      } else {
         this.resetTable()
      }
   }

   doSave(callback?: () => any) {
      var toupdate = this.items
         .filter((i: any) => {
            return i && i.dirty
         })
         .map((et) => {
            return {
               practiceSkey: et.practiceSkey,
               origSourceSkey: et.origSourceSkey,
               employeeTypeSkey: et.employeeTypeSkey,
               assignRevenueToSkey: et.assignRevenueToSkey,
               dayforceId: et.dayforceId,
               docComp: et.docComp,
            } as any
         })
      console.log(toupdate)
      console.log(
         this.items.filter((i: any) => {
            return i && i.dirty
         })
      )
      if (toupdate && toupdate.length) {
         this.saving = true
         this.employeeTypeMapClient.updateEmployeeMappings(toupdate).subscribe(
            (r) => {
               this.items.forEach((i: any) => {
                  if (i) i.dirty = false
               })
               this.isDirty = false
               this.saving = false
               this.toastrSvc.success('Updated Successfully')
               if (callback) callback()
            },
            (e) => {
               console.log(e)
            }
         )
      }
   }

   onSave() {
      this.doSave()
   }
}
